import React, { useCallback, useEffect, useState } from "react";
import { Select, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  AppDispatch,
  getReportLevelOptionListRequest,
} from "@src/redux";
import * as TYPES from "@src/redux/types";

interface ReportLevelFilterProps {
  /** The client whose tree we are drilling into. Resets the chain when it changes. */
  rootLevelOrganizationId?: string;
  /** Fires with the deepest selected organization, or undefined at the root. */
  onOrganizationChange: (organizationId?: string) => void;
}

/** One rung of the drill-down: a level's options plus the current selection. */
type LevelRung = {
  levelId?: string;
  levelName?: string;
  items: TYPES.ReportRootLevel[];
  selectedId?: string;
};

/**
 * Cascading level filter beneath the Root Level select: City -> Zone -> Ward
 * -> ... , one dropdown per level, each listing the organizations under the
 * previous selection.
 *
 * The chain is held in local state rather than redux because every rung hits
 * the same endpoint — a shared single-result slice would have the rungs
 * overwriting each other's options as they resolve.
 */
const ReportLevelFilter: React.FC<ReportLevelFilterProps> = ({
  rootLevelOrganizationId,
  onOrganizationChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [rungList, setRungList] = useState<LevelRung[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchRung = useCallback(
    async (parentOrganizationId: string): Promise<LevelRung | null> => {
      const result = await dispatch(
        getReportLevelOptionListRequest({ parentOrganizationId })
      ).unwrap();
      // No children means we have reached the bottom of the tree; the caller
      // stops adding selects.
      if (!result?.items || result.items.length === 0) return null;
      return {
        levelId: result.levelId,
        levelName: result.levelName,
        items: result.items,
        selectedId: undefined,
      };
    },
    [dispatch]
  );

  // Root Level changed: drop the whole chain and load the first rung under it.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setRungList([]);
      if (!rootLevelOrganizationId) return;
      setLoading(true);
      try {
        const rung = await fetchRung(rootLevelOrganizationId);
        if (!cancelled && rung) setRungList([rung]);
      } catch {
        if (!cancelled) setRungList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [rootLevelOrganizationId, fetchRung]);

  const handleChange = async (index: number, organizationId?: string) => {
    // Truncate everything below the rung being changed, then re-extend.
    const nextRungList = rungList
      .slice(0, index + 1)
      .map((rung, i) =>
        i === index ? { ...rung, selectedId: organizationId } : rung
      );
    setRungList(nextRungList);

    // Clearing a rung falls back to the deepest selection still standing.
    const deepest = [...nextRungList]
      .reverse()
      .find((rung) => rung.selectedId)?.selectedId;
    onOrganizationChange(deepest ?? undefined);

    if (!organizationId) return;

    setLoading(true);
    try {
      const rung = await fetchRung(organizationId);
      if (rung) setRungList([...nextRungList, rung]);
    } catch {
      // Leave the chain as-is; the selection above still applies.
    } finally {
      setLoading(false);
    }
  };

  if (!rootLevelOrganizationId || rungList.length === 0) {
    return loading ? (
      <div className="report-filter-field report-level-loading">
        <Spin size="small" />
      </div>
    ) : null;
  }

  return (
    <>
      {rungList.map((rung, index) => (
        <div className="report-filter-field" key={rung.levelId ?? index}>
          <label className="report-filter-label">
            {rung.levelName || t("select_level") || "Level"}
          </label>
          <Select
            className="report-root-level-select"
            value={rung.selectedId}
            onChange={(value) => handleChange(index, value)}
            placeholder={`${t("select") || "Select"} ${rung.levelName ?? ""}`.trim()}
            options={rung.items.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
            showSearch
            optionFilterProp="label"
            allowClear
            loading={loading && index === rungList.length - 1}
          />
        </div>
      ))}
    </>
  );
};

export default ReportLevelFilter;
